import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { Eye, FileText, Image as ImageIcon, Paperclip, Trash2, Upload } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { apiClient } from '@/lib/api/client'
import type { ParentTicketAttachmentRow } from '@/types/ticket'
import {
  attachmentFileExtension,
  attachmentFileKind,
  formatFileSize,
  type AttachmentFileKind,
} from '@/lib/utils/parentTicketAttachments'

type Props = {
  rows: ParentTicketAttachmentRow[]
  onAddFiles: (files: File[]) => void
  onRemoveRow: (index: number) => void
  ticketId?: string | null
  readOnly?: boolean
}

const ACCEPT = '.pdf,.doc,.docx,image/*'

type LoadedPreview = {
  blobUrl: string
  fileName: string
  kind: AttachmentFileKind
}

function getRowSize(row: ParentTicketAttachmentRow): string {
  if (row.file?.size != null) return formatFileSize(row.file.size)
  return '—'
}

async function fetchSavedAttachmentFile(ticketId: string, attachmentId: number): Promise<LoadedPreview> {
  const apiBaseUrl = apiClient.defaults.baseURL ?? ''
  const apiUrl = `${apiBaseUrl}/tickets/${ticketId}/attachments/${attachmentId}/file`
  const res = await apiClient.get(apiUrl, { responseType: 'blob' })
  const blob = res.data as Blob
  const fileName = String(res.headers?.['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1] ?? 'attachment')
  return {
    blobUrl: URL.createObjectURL(blob),
    fileName,
    kind: attachmentFileKind(fileName, blob.type),
  }
}

function AttachmentPreviewModal({
  isOpen,
  onClose,
  preview,
}: {
  isOpen: boolean
  onClose: () => void
  preview: LoadedPreview
}) {
  const colors = useThemeColors()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={preview.kind === 'image' ? '4xl' : '6xl'} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader fontSize="md" pr={12}>
          {preview.fileName}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {preview.kind === 'image' ? (
            <Image
              src={preview.blobUrl}
              alt={preview.fileName}
              maxW="100%"
              maxH="70vh"
              objectFit="contain"
              mx="auto"
              borderRadius="md"
            />
          ) : preview.kind === 'pdf' ? (
            <Box
              as="iframe"
              src={preview.blobUrl}
              title={preview.fileName}
              w="100%"
              h={{ base: '60vh', md: '70vh' }}
              border="1px solid"
              borderColor={colors.borderLight}
              borderRadius="md"
            />
          ) : (
            <VStack spacing={4} py={8}>
              <FileText size={40} aria-hidden />
              <Text fontSize="sm" color={colors.textSecondary} textAlign="center">
                Preview is not available for this file type.
              </Text>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function AttachmentCard({
  row,
  index,
  readOnly,
  ticketId,
  onRemove,
}: {
  row: ParentTicketAttachmentRow
  index: number
  readOnly: boolean
  ticketId?: string | null
  onRemove: (index: number) => void
}) {
  const colors = useThemeColors()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [preview, setPreview] = useState<LoadedPreview | null>(null)
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)
  const previewRef = useRef<LoadedPreview | null>(null)

  const fileName = row.file?.name ?? row.fileName ?? 'Attachment'
  const kind = attachmentFileKind(fileName, row.file?.type)
  const ext = attachmentFileExtension(fileName)
  const canPreview = kind === 'image' || kind === 'pdf'

  const revokePreview = useCallback(() => {
    if (previewRef.current?.blobUrl) {
      URL.revokeObjectURL(previewRef.current.blobUrl)
    }
    previewRef.current = null
    setPreview(null)
  }, [])

  useEffect(() => {
    if (!row.file || kind !== 'image') {
      setThumbnailUrl(null)
      return
    }

    const url = URL.createObjectURL(row.file)
    setThumbnailUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [row.file, kind])

  useEffect(() => () => revokePreview(), [revokePreview])

  const handlePreview = async () => {
    if (row.file) {
      const next: LoadedPreview = {
        blobUrl: URL.createObjectURL(row.file),
        fileName: row.file.name,
        kind: attachmentFileKind(row.file.name, row.file.type),
      }
      previewRef.current = next
      setPreview(next)
      onOpen()
      return
    }

    if (!ticketId || !row.id) return

    setLoadingPreview(true)
    try {
      const next = await fetchSavedAttachmentFile(ticketId, row.id)
      next.fileName = fileName
      next.kind = kind
      previewRef.current = next
      setPreview(next)
      onOpen()
    } catch {
      // Preview unavailable
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleClosePreview = () => {
    onClose()
    revokePreview()
  }

  return (
    <>
      <HStack
        p={3}
        border="1px solid"
        borderColor={colors.borderLight}
        borderRadius="lg"
        bg="white"
        spacing={3}
        align="center"
      >
        <Box
          w="44px"
          h="44px"
          borderRadius="md"
          bg={colors.inputBg ?? 'gray.50'}
          display="flex"
          alignItems="center"
          justifyContent="center"
          overflow="hidden"
          flexShrink={0}
        >
          {thumbnailUrl ? (
            <Image src={thumbnailUrl} alt="" w="44px" h="44px" objectFit="cover" />
          ) : kind === 'image' ? (
            <ImageIcon size={20} />
          ) : (
            <FileText size={20} />
          )}
        </Box>
        <Box flex="1" minW={0}>
          <Text fontSize="sm" fontWeight="600" noOfLines={1}>
            {fileName}
          </Text>
          <Text fontSize="xs" color={colors.textSecondary}>
            {ext} · {getRowSize(row)}
          </Text>
          {row.fileName && !row.file ? (
            <Text fontSize="xs" color={colors.textSecondary}>
              Existing attachment
            </Text>
          ) : null}
        </Box>
        <HStack spacing={1} flexShrink={0}>
          {canPreview ? (
            <Tooltip label="Preview" hasArrow>
              <IconButton
                aria-label={`Preview ${fileName}`}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                icon={loadingPreview ? <Spinner size="sm" /> : <Eye size={16} />}
                isDisabled={loadingPreview || (!row.file && !(ticketId && row.id))}
                onClick={() => void handlePreview()}
              />
            </Tooltip>
          ) : null}
          {!readOnly ? (
            <IconButton
              aria-label={`Remove ${fileName}`}
              size="sm"
              variant="ghost"
              colorScheme="red"
              icon={<Trash2 size={14} />}
              onClick={() => onRemove(index)}
            />
          ) : null}
        </HStack>
      </HStack>

      {preview && isOpen ? (
        <AttachmentPreviewModal isOpen={isOpen} onClose={handleClosePreview} preview={preview} />
      ) : null}
    </>
  )
}

export function ParentTicketAttachmentSection({
  rows,
  onAddFiles,
  onRemoveRow,
  ticketId,
  readOnly = false,
}: Props) {
  const colors = useThemeColors()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleFiles = useCallback((fileList: FileList | null) => {
    if (!fileList?.length) return
    onAddFiles(Array.from(fileList))
  }, [onAddFiles])

  const onDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    if (readOnly) return
    handleFiles(event.dataTransfer.files)
  }, [handleFiles, readOnly])

  return (
    <Box mt={3}>
      {!readOnly ? (
        <Box
          p={6}
          border="2px dashed"
          borderColor={isDragging ? colors.primary : colors.borderLight}
          borderRadius="lg"
          bg={isDragging ? 'blue.50' : colors.inputBg ?? 'gray.50'}
          textAlign="center"
          cursor="pointer"
          transition="all 0.15s ease"
          onDragEnter={(e) => {
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={(e) => {
            e.preventDefault()
            setIsDragging(false)
          }}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept={ACCEPT}
            onChange={(e) => {
              handleFiles(e.target.files)
              e.target.value = ''
            }}
          />
          <VStack spacing={2}>
            <Box color={colors.primary}>
              {isDragging ? <Upload size={28} /> : <Paperclip size={28} />}
            </Box>
            <Text fontSize="sm" fontWeight="600" color={colors.text}>
              Drag files here or click to browse
            </Text>
            <Text fontSize="xs" color={colors.textSecondary}>
              PDF, Word, or image files
            </Text>
          </VStack>
        </Box>
      ) : null}

      <VStack align="stretch" spacing={2} mt={rows.length > 0 ? 3 : 0}>
        {rows.map((row, idx) => (
          <AttachmentCard
            key={row.id ?? `attachment-${idx}-${row.fileName ?? row.file?.name ?? 'pending'}`}
            row={row}
            index={idx}
            readOnly={readOnly}
            ticketId={ticketId}
            onRemove={onRemoveRow}
          />
        ))}
      </VStack>

      {rows.length === 0 ? (
        <Text fontSize="sm" color={colors.textSecondary} mt={readOnly ? 0 : 3}>
          No attachments added yet.
        </Text>
      ) : null}
    </Box>
  )
}

export function attachmentRowsSummary(rows: ParentTicketAttachmentRow[]): string {
  return rows
    .map((row) => row.fileName ?? row.file?.name ?? 'pending')
    .join(', ')
}
